import { useState, useEffect, useCallback } from "react";
import { fetchRecentPrevalence, downloadRecentPrevalenceCsv } from "../api";
import { CISA_SECTORS, SECTOR_EMOJI } from "../utils/sectors";
import { levelRank, PREVALENCE_LEVELS, LEVEL_COLOR } from "../utils/prevalence";
import { fmtTs } from "../utils/time";

// Saved cross-cutting questions. Each entry renders its own controls and
// table, so adding a second query is one more entry here plus its component —
// the tab bar, routing and empty/error handling are already generic.
const QUERIES = [
  { key: "recent-prevalence", label: "Recent Prevalence" },
];

// Windows worth asking for. "Last 12 hours" is the tightest useful one: the
// import and prevalence passes run on timers, so anything shorter mostly
// reports CVEs the pipeline has not reached yet.
const DAY_OPTIONS = [
  { value: 0.5, label: "Last 12 hours" },
  { value: 1, label: "Last 24 hours" },
  { value: 3, label: "Last 3 days" },
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
];

// new/modified are disjoint, matching askvinnie's mutually exclusive flags:
// a CVE published inside the window is "new"; "modified" means published
// before it and edited inside it.
// "All" is the server ceiling, not infinity — the endpoint caps at 2000 and
// reports meta.truncated when a window holds more. Labelled with the number
// so the cap is visible before you pick it rather than after.
const LIMIT_MAX = 2000;
const LIMIT_OPTIONS = [
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 250, label: "250" },
  { value: 500, label: "500" },
  { value: 1000, label: "1000" },
  { value: LIMIT_MAX, label: `All (max ${LIMIT_MAX})` },
];

const KIND_OPTIONS = [
  { value: "new", label: "Newly published" },
  { value: "modified", label: "Modified since publication" },
  { value: "both", label: "Both" },
];

function LevelBadge({ level }) {
  if (!level) return <span className="jobs-muted">unassessed</span>;
  return <span className={`products-badge products-badge-sm products-badge-${level}`}>{level}</span>;
}

function driverName(driver) {
  if (!driver) return null;
  return [driver.vendor, driver.product].filter(Boolean).join(" ") || null;
}

// Worst-first: the reader wants the sectors this CVE is most present in.
function topSectors(sectors, n = 3) {
  return Object.entries(sectors || {})
    .sort((a, b) => levelRank(b[1]?.level ?? b[1]) - levelRank(a[1]?.level ?? a[1]))
    .slice(0, n);
}

// Recency first, matching the order the endpoint already returns, so the
// initial view is the one the query asked for rather than a ranking nobody
// requested. Every header sorts from there.
// Three readings of one fetch. The endpoint already returns every sector on
// every row, so switching view costs no query — only rendering changes.
const VIEWS = [
  { key: "summary", label: "Summary", hint: "One row per CVE, top sectors only" },
  { key: "matrix", label: "CVE \u00D7 sector", hint: "Every sector as a column" },
  { key: "rollup", label: "Sector rollup", hint: "Which sectors this window hits hardest" },
  { key: "scores", label: "Scoring", hint: "CVSS v3.1/v4.0, SSVC and CWE per CVE" },
];

// Matches the --severity-* ramp the rest of the dashboard uses, so a score
// here and the same score on the CVE page read as the same colour.
const SEVERITY_COLOR = {
  CRITICAL: "var(--severity-critical)", HIGH: "var(--severity-high)",
  MEDIUM: "var(--severity-medium)", LOW: "var(--severity-low)",
  NONE: "var(--severity-none)",
};

// Track < Track* < Attend < Act — an escalation, so it sorts by urgency
// rather than alphabetically (which would put Act first and Track* last).
const SSVC_DECISION_LEVEL = { "Track": 0, "Track*": 1, "Attend": 2, "Act": 3 };
const SSVC_DECISION_COLOR = {
  "Act": "var(--severity-critical)", "Attend": "var(--severity-high)",
  "Track*": "var(--severity-medium)", "Track": "var(--severity-low)",
};

// Every source that scored this type, for the hover. The winner is already
// first — for CVSS/SSVC the endpoint ranks Vinnie's own score (gemma) above
// the upstream opinions above other models (for CWE: COMPOSITE, then LLM,
// then upstream) — so disagreement reads top-down.
function sourcesTip(s) {
  if (!s?.sources?.length) return undefined;
  return s.sources.map((x) => {
    const who = [x.source, x.model].filter(Boolean).join("/");
    const what = x.score != null ? `${x.score} ${x.severity}` : (x.decision || x.value);
    return `${who}: ${what}`;
  }).join("\n");
}

// Single letters because a sector matrix is 17 columns wide and full level
// names would make it unreadable. Legend renders under the table.
const LEVEL_CODE = { ubiquitous: "U", widespread: "W", moderate: "M", low: "L", unknown: "?" };

// Only the sectors this result set actually scored. Seventeen columns of
// which four are populated is not a table anyone can read, and the prevalence
// guide is explicit that most products are unknown for most sectors — so the
// empty columns are the expected case, not a data gap worth displaying.
function presentSectors(rows) {
  const seen = new Set();
  for (const r of rows) for (const k of Object.keys(r.sectors || {})) seen.add(k);
  return [...seen].sort((a, b) =>
    (CISA_SECTORS[a] || a).localeCompare(CISA_SECTORS[b] || b));
}

// Levels actually present, worst-first, so the rollup does not carry a column
// of zeros for a level nothing reached.
function presentLevels(rows) {
  const seen = new Set();
  for (const r of rows) {
    for (const e of Object.values(r.sectors || {})) {
      const lvl = e?.level ?? e;
      if (lvl) seen.add(lvl);
    }
  }
  return [...PREVALENCE_LEVELS].reverse().filter((l) => seen.has(l));
}

// A blank sector cell can mean three different things, and collapsing them
// loses the most interesting one. The prevalence guide tells the model to
// express "no basis for this sector" by OMITTING the sector, so for a CVE
// whose products were actually assessed an absent sector is a real judgement
// — unknown — not missing data. When nothing was scored, the same blank says
// only that nobody has looked yet. Sector levels are never literally
// "unknown" in the data; that state exists solely as an absence.
function cellState(row, slug) {
  const e = row.sectors?.[slug];
  const lvl = e?.level ?? e;
  if (lvl) return { kind: "scored", level: lvl, driver: e?.driver };
  if ((row.scored ?? 0) > 0) return { kind: "unknown" };
  if (row.snapshot_at) return { kind: "noproducts" };
  return { kind: "unassessed" };
}

const CELL_META = {
  unknown: { glyph: "?", color: "var(--text-muted)",
             tip: "unknown — products assessed, no basis for this sector" },
  noproducts: { glyph: "\u00B7", color: "var(--border)",
                tip: "no affected product resolved, so no sector could be assessed" },
  unassessed: { glyph: "\u00B7", color: "var(--border)",
                tip: "CVE not assessed yet — no prevalence snapshot" },
};

// One row per sector: how many of the window's CVEs land at each level there.
// A CVE counts once per sector, at the level that sector scored — the counts
// across a sector's levels therefore sum to its CVE total.
function sectorRollup(rows, levels) {
  // Only CVEs with a scored product can express "unknown" about a sector;
  // the rest have no opinion to record. That makes this the honest
  // denominator for the unknown column.
  const assessable = rows.filter((r) => (r.scored ?? 0) > 0).length;
  const by = new Map();
  for (const r of rows) {
    for (const [slug, e] of Object.entries(r.sectors || {})) {
      const lvl = e?.level ?? e;
      if (!lvl) continue;
      let row = by.get(slug);
      if (!row) by.set(slug, (row = { slug, total: 0, cves: [], counts: {} }));
      row.counts[lvl] = (row.counts[lvl] || 0) + 1;
      row.total += 1;
      row.cves.push(r.cve);
    }
  }
  for (const row of by.values()) {
    row.assessable = assessable;
    row.unknown = assessable - row.total;
  }
  // Worst-first down the scale, so a sector with one ubiquitous CVE outranks
  // one with ten lows — which is the prioritisation question being asked.
  return [...by.values()].sort((a, b) => {
    for (const l of levels) {
      const d = (b.counts[l] || 0) - (a.counts[l] || 0);
      if (d) return d;
    }
    return b.total - a.total;
  });
}

const DEFAULT_SORT = { key: "published", dir: "desc" };

function sortRows(rows, sort) {
  const { key, dir } = sort;
  const sign = dir === "asc" ? 1 : -1;
  const value = (r) => {
    switch (key) {
      case "cve": return r.cve.toLowerCase();
      // A scale, not a string: "widespread" has to outrank "moderate", which
      // alphabetical order gets exactly backwards. levelRank returns -1 for
      // an unassessed CVE, putting it below "unknown" — which is right, since
      // "nobody has asked" is less information than "asked, came back unknown".
      case "prevalence": return levelRank(r.global);
      case "driver": return (driverName(r.driver) || "").toLowerCase();
      case "sectors": return Object.keys(r.sectors || {}).length;
      // Sort by what was actually assessed, not by how many products the CNA
      // happened to list — the assessed count is the one that bounds how much
      // the prevalence level is worth.
      case "products": return r.scored ?? 0;
      case "cna": return (r.cna || "").toLowerCase();
      case "published": return r.published_at || "";
      case "cvss31": return r.scores?.["CVSSv3.1"]?.score ?? -1;
      case "cvss40": return r.scores?.["CVSSv4.0"]?.score ?? -1;
      case "ssvc": {
        const d = r.scores?.SSVC?.decision;
        return d in SSVC_DECISION_LEVEL ? SSVC_DECISION_LEVEL[d] : -1;
      }
      // Numeric, so CWE-79 sorts near CWE-89 rather than between CWE-787 and
      // CWE-798 as a string would.
      case "cwe": {
        const m = /CWE-(\d+)/.exec(r.scores?.CWE?.value || "");
        return m ? Number(m[1]) : -1;
      }
      default: {
        // "sector:<slug>" — sort the matrix by one sector's level. Same
        // ordinal rule as the global column; a blank cell ranks below "low".
        if (key.startsWith("sector:")) {
          const e = r.sectors?.[key.slice(7)];
          return levelRank(e?.level ?? e);
        }
        return "";
      }
    }
  };
  return [...rows].sort((a, b) => {
    const av = value(a), bv = value(b);
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    // Ties are common on every column here (whole pages share one level, or
    // one CNA). A fixed second key stops rows shuffling between renders.
    return a.cve < b.cve ? -1 : 1;
  });
}

function SortHeader({ label, sortKey, sort, setSort, align = "left", title }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => setSort(active
        ? { key: sortKey, dir: sort.dir === "asc" ? "desc" : "asc" }
        // Levels, counts and dates are most useful largest-first; names read A-Z.
        : { key: sortKey, dir: ["cve", "driver", "cna"].includes(sortKey) ? "asc" : "desc" })}
      style={{
        padding: "6px 8px", cursor: "pointer", whiteSpace: "nowrap",
        textAlign: align, color: active ? "var(--accent)" : undefined,
      }}
      title={title}
    >
      {label}{active ? (sort.dir === "asc" ? " \u25B2" : " \u25BC") : ""}
    </th>
  );
}

// CSV export lives server-side: /api/queries/recent-prevalence.csv streams
// the whole window off a cursor rather than the capped page the table holds.
// The column shapes for each view are defined there and deliberately not
// duplicated here — two definitions of the same schema is how an export
// starts quietly disagreeing with the table it came from.

function RecentPrevalenceQuery() {
  const [days, setDays] = useState(1);
  const [kind, setKind] = useState("new");
  const [scoredOnly, setScoredOnly] = useState(false);
  const [limit, setLimit] = useState(100);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [view, setView] = useState("summary");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchRecentPrevalence({ days, kind, limit, scoredOnly })
      .then((d) => setData(d))
      .catch((e) => setError(e.message || "Query failed"))
      .finally(() => setLoading(false));
  }, [days, kind, limit, scoredOnly]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const meta = data?.meta || {};
  const sorted = sortRows(rows, sort);
  const sectors = presentSectors(rows);
  const levels = presentLevels(rows);
  const rollup = sectorRollup(rows, levels);


  return (
    <div>
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header">Recent Prevalence</div>
        <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)" }}>
          Software prevalence rollup for CVEs in a recent window — the CVE-level
          level, and the affected product that set it.
        </div>
        <div style={{
          padding: "0 12px 12px", display: "flex", gap: 12,
          alignItems: "center", flexWrap: "wrap",
        }}>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            Window
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {DAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            Kind
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            Max rows
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              {LIMIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={scoredOnly}
              onChange={(e) => setScoredOnly(e.target.checked)}
            />
            Assessed only
          </label>
          <button className="flush-btn" onClick={load} disabled={loading}>
            {loading ? "Running…" : "Refresh"}
          </button>
          <span style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-secondary)", marginRight: 4 }}>View</span>
            {VIEWS.map((v) => (
              <button
                key={v.key}
                className={`table-tab ${view === v.key ? "active" : ""}`}
                onClick={() => setView(v.key)}
                title={v.hint}
              >
                {v.label}
              </button>
            ))}
          </span>
        </div>
      </div>

      {error && (
        <div className="panel" style={{ marginBottom: 12, padding: 12, color: "var(--severity-high)" }}>
          {error}
        </div>
      )}

      <div className="panel">
        <div className="panel-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {loading ? "Loading…" : `${rows.length} CVE${rows.length === 1 ? "" : "s"}`}
          {!loading && meta.truncated && (
            <span
              style={{ fontSize: 11, fontWeight: 600, color: "var(--severity-high)" }}
              title={`This window holds ${(meta.matched || 0).toLocaleString()} CVEs; the server caps a page at ${LIMIT_MAX}. Narrow the window for a complete picture — the rollup and sector counts describe only these ${rows.length} rows.`}
            >
              partial — {(meta.matched || 0).toLocaleString()} in window
            </span>
          )}
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)", fontWeight: 400 }}>
            {meta.matched != null && `${meta.matched.toLocaleString()} in window`}
            {meta.truncated && ` · truncated to ${meta.limit}`}
            {meta.unanalysed > 0 && ` · ${meta.unanalysed} not yet assessed`}
          </span>
          <button
            className="raw-json-link"
            onClick={async () => {
              setExporting(true);
              try {
                await downloadRecentPrevalenceCsv({ days, kind, scoredOnly, view });
              } catch (e) {
                setError(e.message || "Export failed");
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting || rows.length === 0}
            title={rows.length
              ? `Download every CVE in this window as CSV — all ${(meta.matched || 0).toLocaleString()}, not just the ${rows.length} shown. Streamed from the server, so it ignores the row cap; ordered by recency rather than the column you sorted by.`
              : "Nothing to export"}
          >
            {exporting ? "Exporting…" : "CSV (all)"}
          </button>
        </div>

        {!loading && view === "scores" && rows.length > 0 ? (
          <>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)", textAlign: "left" }}>
                  <SortHeader label="CVE" sortKey="cve" sort={sort} setSort={setSort} />
                  <SortHeader label="CVSS v3.1" sortKey="cvss31" sort={sort} setSort={setSort}
                              align="right" title="Base score from the preferred source" />
                  <SortHeader label="CVSS v4.0" sortKey="cvss40" sort={sort} setSort={setSort}
                              align="right" title="Base score from the preferred source" />
                  <SortHeader label="SSVC" sortKey="ssvc" sort={sort} setSort={setSort}
                              title="Sorts by urgency: Track, Track*, Attend, Act" />
                  <SortHeader label="CWE" sortKey="cwe" sort={sort} setSort={setSort}
                              title="Sorts numerically" />
                  <SortHeader label="Prevalence" sortKey="prevalence" sort={sort} setSort={setSort} />
                  <SortHeader label="CNA" sortKey="cna" sort={sort} setSort={setSort} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const sc = r.scores || {};
                  const v31 = sc["CVSSv3.1"], v40 = sc["CVSSv4.0"];
                  const ssvc = sc.SSVC, cwe = sc.CWE;
                  const cvssCell = (s) => s == null ? <span className="jobs-muted">—</span> : (
                    <span className="products-tooltip" data-tooltip={sourcesTip(s)}
                          style={{ color: SEVERITY_COLOR[s.severity] || "var(--text-secondary)", fontWeight: 600 }}>
                      {s.score ?? "?"}
                      {s.count > 1 && (
                        <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> ·{s.count}</span>
                      )}
                    </span>
                  );
                  return (
                    <tr key={r.cve} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        <a href={`/cve/${r.cve}`} style={{ color: "#58a6ff", textDecoration: "none" }}>
                          {r.cve}
                        </a>
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{cvssCell(v31)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{cvssCell(v40)}</td>
                      <td style={{ padding: "6px 8px" }}>
                        {ssvc?.decision ? (
                          <span className="products-tooltip" data-tooltip={sourcesTip(ssvc)}
                                style={{ color: SSVC_DECISION_COLOR[ssvc.decision], fontWeight: 600 }}>
                            {ssvc.decision}
                            {ssvc.count > 1 && (
                              <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> ·{ssvc.count}</span>
                            )}
                          </span>
                        ) : <span className="jobs-muted">—</span>}
                      </td>
                      <td style={{ padding: "6px 8px", color: "var(--text-secondary)" }}>
                        {cwe?.value ? (
                          <span className="products-tooltip" data-tooltip={sourcesTip(cwe)}>
                            {cwe.value}
                            {cwe.count > 1 && (
                              <span style={{ color: "var(--text-muted)" }}> ·{cwe.count}</span>
                            )}
                          </span>
                        ) : <span className="jobs-muted">—</span>}
                      </td>
                      <td style={{ padding: "6px 8px" }}><LevelBadge level={r.global} /></td>
                      <td style={{ padding: "6px 8px", color: "var(--text-secondary)" }}>
                        {r.cna || <span className="jobs-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-muted)" }}>
              One value per type, from the highest-ranked source that scored it —
              For CVSS and SSVC, Vinnie's own score (gemma) first, then
              NVD/CISA/CNA/Vendor, then other models; for CWE, the composite
              first, then Vinnie's LLM work, then NVD/CISA/CNA/Vendor.
              A <strong>·n</strong> suffix counts how many sources scored that
              type; hover to see each one, so a disagreement is visible without
              leaving the table. A dash means nothing scored it at all.
            </div>
          </>
        ) : !loading && view === "rollup" && rows.length > 0 ? (
          <>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)", textAlign: "left" }}>
                  <th style={{ padding: "6px 8px" }}>Sector</th>
                  {levels.map((l) => (
                    <th key={l} style={{ padding: "6px 8px", textAlign: "right", color: LEVEL_COLOR[l] }}>
                      {l}
                    </th>
                  ))}
                  <th style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-muted)" }}
                      title="Assessed CVEs that recorded no basis for this sector">
                    unknown
                  </th>
                  <th style={{ padding: "6px 8px", textAlign: "right" }}>CVEs</th>
                </tr>
              </thead>
              <tbody>
                {rollup.map((r) => (
                  <tr key={r.slug} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                      <span aria-hidden="true">{SECTOR_EMOJI[r.slug]} </span>
                      {CISA_SECTORS[r.slug] || r.slug}
                    </td>
                    {levels.map((l) => (
                      <td key={l} style={{
                        padding: "6px 8px", textAlign: "right",
                        color: r.counts[l] ? LEVEL_COLOR[l] : "var(--border)",
                        fontWeight: r.counts[l] ? 600 : 400,
                      }}>
                        {r.counts[l] || "·"}
                      </td>
                    ))}
                    <td style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-muted)" }}
                        title={`${r.unknown} of ${r.assessable} assessable CVEs recorded no basis here`}>
                      {r.unknown || "·"}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-secondary)" }}>
                      {r.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-muted)" }}>
              Ordered worst-first down the scale: one ubiquitous CVE outranks ten
              low ones. A CVE counts once per sector it scored, so a sector's
              level counts sum to its CVE total, and one CVE appears in several
              sectors. The unknown column counts assessed CVEs that recorded no
              basis for that sector — the guide asks the model to express that
              by omission, so it is a judgement, not missing data.
              {" "}{sectors.length} of 17 sectors appear; the other {17 - sectors.length} were
              unknown for every CVE in this window.
            </div>
          </>
        ) : !loading && view === "matrix" && rows.length > 0 ? (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)", textAlign: "left" }}>
                    <SortHeader label="CVE" sortKey="cve" sort={sort} setSort={setSort} />
                    <SortHeader label="Global" sortKey="prevalence" sort={sort} setSort={setSort} />
                    <SortHeader label="Driven by" sortKey="driver" sort={sort} setSort={setSort}
                                title="The affected product whose prevalence set this CVE's global level" />
                    {sectors.map((slug) => (
                      <SortHeader
                        key={slug}
                        label={SECTOR_EMOJI[slug] || (CISA_SECTORS[slug] || slug).slice(0, 3)}
                        sortKey={`sector:${slug}`}
                        sort={sort}
                        setSort={setSort}
                        align="center"
                        title={CISA_SECTORS[slug] || slug}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr key={r.cve} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        <a href={`/cve/${r.cve}`} style={{ color: "#58a6ff", textDecoration: "none" }}>
                          {r.cve}
                        </a>
                      </td>
                      <td style={{ padding: "6px 8px" }}><LevelBadge level={r.global} /></td>
                      <td style={{ padding: "6px 8px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                        {driverName(r.driver) || <span className="jobs-muted">—</span>}
                      </td>
                      {sectors.map((slug) => {
                        const st = cellState(r, slug);
                        const name = CISA_SECTORS[slug] || slug;
                        const meta = CELL_META[st.kind];
                        const drv = driverName(st.driver);
                        return (
                          <td
                            key={slug}
                            className="products-tooltip"
                            data-tooltip={st.kind === "scored"
                              ? `${name}: ${st.level}${drv ? ` — ${drv}` : ""}`
                              : `${name}: ${meta.tip}`}
                            style={{
                              padding: "6px 8px", textAlign: "center", fontWeight: 600,
                              color: st.kind === "scored" ? LEVEL_COLOR[st.level] : meta.color,
                            }}
                          >
                            {st.kind === "scored" ? (LEVEL_CODE[st.level] || "?") : meta.glyph}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "8px 12px", fontSize: 11, color: "var(--text-muted)" }}>
              {levels.map((l) => (
                <span key={l} style={{ marginRight: 12 }}>
                  <strong style={{ color: LEVEL_COLOR[l] }}>{LEVEL_CODE[l]}</strong> = {l}
                </span>
              ))}
              <span style={{ marginRight: 12 }}>
                <strong style={{ color: CELL_META.unknown.color }}>?</strong> = unknown
                <span style={{ opacity: 0.8 }}> (assessed, no basis for this sector)</span>
              </span>
              <span style={{ marginRight: 12 }}>
                <strong style={{ color: CELL_META.noproducts.color }}>·</strong> = nothing assessed
              </span>
              <span>
                · showing the {sectors.length} of 17 sectors this window scored.
                “Driven by” is the product that set the <em>global</em> level; a
                sector can be driven by a different one, which its cell tooltip names.
              </span>
            </div>
          </>
        ) : !loading && rows.length === 0 ? (
          <div style={{ padding: 16, color: "var(--text-muted)", fontSize: 13 }}>
            No CVEs in this window.{" "}
            {scoredOnly && "Try clearing “Assessed only” — prevalence snapshots lag publication."}
          </div>
        ) : (
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)", textAlign: "left" }}>
                <SortHeader label="CVE" sortKey="cve" sort={sort} setSort={setSort} />
                <SortHeader label="Prevalence" sortKey="prevalence" sort={sort} setSort={setSort}
                            title="Sorts along the scale, not alphabetically" />
                <SortHeader label="Driven by" sortKey="driver" sort={sort} setSort={setSort} />
                <SortHeader label="Top sectors" sortKey="sectors" sort={sort} setSort={setSort}
                            title="Sorts by how many sectors scored a level" />
                <SortHeader label="Products" sortKey="products" sort={sort} setSort={setSort}
                            align="right" title="Sorts by the assessed count" />
                <SortHeader label="CNA" sortKey="cna" sort={sort} setSort={setSort} />
                <SortHeader label="Published" sortKey="published" sort={sort} setSort={setSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const driver = driverName(r.driver);
                const sectors = topSectors(r.sectors);
                return (
                  <tr key={r.cve} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                      <a href={`/cve/${r.cve}`} style={{ color: "#58a6ff", textDecoration: "none" }}>
                        {r.cve}
                      </a>
                    </td>
                    <td style={{ padding: "6px 8px" }}><LevelBadge level={r.global} /></td>
                    <td style={{ padding: "6px 8px", color: "var(--text-secondary)" }}>
                      {driver || <span className="jobs-muted">—</span>}
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      {sectors.length === 0
                        ? <span className="jobs-muted">—</span>
                        : sectors.map(([slug, entry]) => {
                            const level = entry?.level ?? entry;
                            return (
                              <span
                                key={slug}
                                className={`products-badge products-badge-sm products-badge-${level} products-tooltip`}
                                data-tooltip={`${CISA_SECTORS[slug] || slug}: ${level}`}
                                style={{ marginRight: 4 }}
                              >
                                <span aria-hidden="true">{SECTOR_EMOJI[slug]}</span>
                              </span>
                            );
                          })}
                    </td>
                    <td style={{
                      padding: "6px 8px", textAlign: "right",
                      color: "var(--text-secondary)", whiteSpace: "nowrap",
                    }}
                      title="assessed / resolved / affected"
                    >
                      {r.scored ?? 0}/{r.resolved ?? 0}/{r.affected_total ?? 0}
                    </td>
                    <td style={{ padding: "6px 8px", color: "var(--text-secondary)" }}>
                      {r.cna || <span className="jobs-muted">—</span>}
                    </td>
                    <td style={{
                      padding: "6px 8px", color: "var(--text-muted)", whiteSpace: "nowrap",
                    }} title={r.published_at || ""}>
                      {r.published_at ? fmtTs(r.published_at) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function QueriesPanel() {
  const [query, setQuery] = useState(QUERIES[0].key);

  return (
    <div>
      <div className="table-tabs">
        {QUERIES.map(({ key, label }) => (
          <button
            key={key}
            className={`table-tab ${query === key ? "active" : ""}`}
            onClick={() => setQuery(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {query === "recent-prevalence" && <RecentPrevalenceQuery />}
    </div>
  );
}
